import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { UploadCloud, ShoppingCart, X, Plus, Minus, CheckCircle, Trash2, Send } from 'lucide-react';
import { promoCategories, promoProducts } from '../data/promoProducts';
import SEO from '../components/SEO';

const BrandingProductsPage = () => {
  const { categorySlug } = useParams();
  const navigate = useNavigate();

  // Product & Modal States
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [quantity, setQuantity] = useState(50);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [showToast, setShowToast] = useState(false);

  // Cart & Quote States
  const [cartItems, setCartItems] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [quoteSuccess, setQuoteSuccess] = useState(false);
  const [quoteForm, setQuoteForm] = useState({
    name: '', email: '', phone: '', company: '', notes: ''
  });

  // Determine active category from URL
  const activeCategoryObj = promoCategories.find(c => c.slug === categorySlug);
  const activeCategoryName = activeCategoryObj ? activeCategoryObj.name : "All";

  // ADD THIS NEW LINE TO FIX THE ERROR:
  const pageTitle = activeCategoryName === "All" ? "Custom Promotional Products" : `${activeCategoryName} Promotional Products`;

  // Filter products based on URL
  const filteredProducts = activeCategoryName === "All"
    ? promoProducts
    : promoProducts.filter(p => p.category === activeCategoryName);

  const handleTabClick = (slug) => {
    if (slug === "all") {
      navigate('/promotional-products');
    } else {
      navigate(`/promotional-products/${slug}`);
    }
  };

  const openModal = (product) => {
    setSelectedProduct(product);
    setQuantity(50);
    setUploadedFile(null);
  };

  const closeModal = () => setSelectedProduct(null);

  const handleFileUpload = (e) => {
    if (e.target.files && e.target.files[0]) {
      setUploadedFile(e.target.files[0].name);
    }
  };

  // Add Item to Cart Array
  const handleAddToCart = () => {
    const newItem = {
      id: Date.now(),
      product: selectedProduct,
      quantity: quantity,
      logo: uploadedFile || 'No logo uploaded'
    };

    setCartItems(prev => [...prev, newItem]);
    closeModal();
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  // Remove Item from Cart
  const handleRemoveItem = (id) => {
    setCartItems(prev => prev.filter(item => item.id !== id));
  };

  // Handle Form Input
  const handleInputChange = (e) => {
    setQuoteForm({ ...quoteForm, [e.target.name]: e.target.value });
  };

  // Submit Quote Logic
  const handleSubmitQuote = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Note: Here is where you would link EmailJS or your backend API to actually send the email to A2zsigns.co@gmail.com
    // For now, we simulate a successful network request
    setTimeout(() => {
      setIsSubmitting(false);
      setQuoteSuccess(true);

      // Auto-close and reset after success
      setTimeout(() => {
        setQuoteSuccess(false);
        setIsCartOpen(false);
        setCartItems([]);
        setQuoteForm({ name: '', email: '', phone: '', company: '', notes: '' });
      }, 3000);
    }, 1500);
  };

  return (
    <>
      <SEO
        title={pageTitle}
        description={`Browse our premium catalog of custom branded ${pageTitle.toLowerCase()} for your next corporate event or marketing campaign.`}
      />

      <div className="pt-32 pb-24 min-h-screen bg-brand-light relative">

        {/* Floating Cart Indicator (Clickable) */}
        <button
          onClick={() => setIsCartOpen(true)}
          className="fixed top-28 right-6 z-40 bg-brand-dark hover:bg-brand-blue text-white px-6 py-3 rounded-full shadow-2xl flex items-center gap-3 border border-brand-cyan/30 transition-colors cursor-pointer"
        >
          <ShoppingCart size={20} className="text-brand-cyan" />
          <span className="font-bold">{cartItems.length} Items</span>
        </button>

        {/* Success Toast */}
        <AnimatePresence>
          {showToast && (
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 50 }}
              className="fixed bottom-10 left-1/2 transform -translate-x-1/2 z-50 bg-green-500 text-white px-6 py-4 rounded-xl shadow-2xl flex items-center gap-3 font-bold"
            >
              <CheckCircle size={24} /> Added to Quote Request!
            </motion.div>
          )}
        </AnimatePresence>

        {/* Main Page Content */}
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          <div className="text-center mb-16">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-4xl md:text-5xl font-heading font-black text-brand-dark mb-4"
            >
              Branding <span className="text-brand-cyan">Products</span>
            </motion.h1>
            <div className="w-24 h-1.5 bg-gradient-to-r from-brand-blue to-brand-cyan mx-auto rounded-full"></div>
            <p className="mt-6 text-gray-600 max-w-2xl mx-auto text-lg">
              High-quality promotional merchandise customized with your logo. Perfect for corporate gifting, marketing campaigns, and events.
            </p>
          </div>

          {/* Dynamic Category Tabs */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            <button
              onClick={() => handleTabClick("all")}
              className={`px-6 py-3 rounded-full font-bold text-sm transition-all duration-300 ${activeCategoryName === "All"
                ? 'bg-brand-cyan text-brand-dark shadow-[0_0_15px_rgba(0,198,255,0.4)]'
                : 'bg-white text-gray-500 hover:bg-gray-100 border border-gray-200'
                }`}
            >
              All Products
            </button>
            {promoCategories.map((category) => (
              <button
                key={category.slug}
                onClick={() => handleTabClick(category.slug)}
                className={`px-6 py-3 rounded-full font-bold text-sm transition-all duration-300 ${activeCategoryName === category.name
                  ? 'bg-brand-cyan text-brand-dark shadow-[0_0_15px_rgba(0,198,255,0.4)]'
                  : 'bg-white text-gray-500 hover:bg-gray-100 border border-gray-200'
                  }`}
              >
                {category.name}
              </button>
            ))}
          </div>

          {/* Product Grid (4 Columns) */}
          <motion.div
            key={categorySlug || "all"}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {filteredProducts.map((product) => (
              <div key={product.id} className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden group hover:shadow-xl transition-shadow flex flex-col h-full">
                <div className="h-56 overflow-hidden bg-gray-100 relative">
                  <img src={product.image} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-brand-dark/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
                <div className="p-6 flex flex-col flex-grow">
                  <h3 className="font-bold text-brand-dark text-lg mb-1 leading-tight">{product.name}</h3>
                  <p className="text-brand-cyan font-black mb-3">As low as {product.price}</p>
                  <p className="text-sm text-gray-500 mb-6 flex-grow">{product.desc}</p>
                  <button
                    onClick={() => openModal(product)}
                    className="w-full py-3 bg-brand-light text-brand-dark border border-brand-cyan font-bold rounded-xl hover:bg-brand-cyan transition-colors duration-300"
                  >
                    Customize & Add
                  </button>
                </div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Cart Side Drawer */}
        <AnimatePresence>
          {isCartOpen && (
            <div className="fixed inset-0 z-[60] flex justify-end">
              {/* Dark Overlay Backdrop */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setIsCartOpen(false)}
                className="absolute inset-0 bg-brand-dark/60 backdrop-blur-sm"
              ></motion.div>

              {/* Sliding Drawer Panel */}
              <motion.div
                initial={{ x: '100%' }}
                animate={{ x: 0 }}
                exit={{ x: '100%' }}
                transition={{ type: "spring", damping: 25, stiffness: 200 }}
                className="relative w-full max-w-md bg-white shadow-2xl h-full flex flex-col z-10"
              >
                {/* Drawer Header */}
                <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                  <div className="flex items-center gap-3">
                    <ShoppingCart className="text-brand-cyan" size={24} />
                    <h2 className="text-xl font-heading font-black text-brand-dark">Quote Request</h2>
                  </div>
                  <button onClick={() => setIsCartOpen(false)} className="p-2 bg-white rounded-full shadow-sm hover:bg-gray-100 text-gray-500 transition-colors">
                    <X size={20} />
                  </button>
                </div>

                {/* Drawer Body (Scrollable) */}
                <div className="flex-1 overflow-y-auto custom-scrollbar p-6">

                  {cartItems.length === 0 ? (
                    <div className="h-full flex flex-col items-center justify-center text-gray-400 space-y-4">
                      <ShoppingCart size={48} className="opacity-20" />
                      <p>Your quote list is currently empty.</p>
                    </div>
                  ) : (
                    <>
                      {/* List of Cart Items */}
                      <div className="space-y-4 mb-8">
                        {cartItems.map((item) => (
                          <div key={item.id} className="flex gap-4 p-4 border border-gray-100 rounded-xl bg-white shadow-sm relative group">
                            <img src={item.product.image} alt={item.product.name} className="w-20 h-20 object-cover rounded-lg bg-gray-50" />
                            <div className="flex-1">
                              <h4 className="font-bold text-brand-dark text-sm leading-tight mb-1">{item.product.name}</h4>
                              <p className="text-xs text-gray-500 mb-1">Qty: <span className="font-bold text-brand-dark">{item.quantity}</span></p>
                              <p className="text-[10px] bg-gray-100 px-2 py-1 rounded inline-block text-gray-600 truncate max-w-[150px]">Logo: {item.logo}</p>
                            </div>
                            <button
                              onClick={() => handleRemoveItem(item.id)}
                              className="absolute top-2 right-2 p-2 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        ))}
                      </div>

                      {/* Quote Form */}
                      <div className="border-t border-gray-100 pt-6">
                        <h3 className="font-bold text-brand-dark mb-4">Your Details</h3>

                        {quoteSuccess ? (
                          <div className="bg-green-50 border border-green-200 text-green-700 p-6 rounded-xl text-center">
                            <CheckCircle size={40} className="mx-auto mb-3 text-green-500" />
                            <p className="font-bold text-lg mb-1">Request Sent!</p>
                            <p className="text-sm">We will email your custom quote shortly.</p>
                          </div>
                        ) : (
                          <form onSubmit={handleSubmitQuote} className="space-y-4">
                            <div>
                              <input required type="text" name="name" value={quoteForm.name} onChange={handleInputChange} placeholder="Full Name *" className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:border-brand-cyan text-sm" />
                            </div>
                            <div>
                              <input required type="email" name="email" value={quoteForm.email} onChange={handleInputChange} placeholder="Email Address *" className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:border-brand-cyan text-sm" />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                              <input required type="tel" name="phone" value={quoteForm.phone} onChange={handleInputChange} placeholder="Phone Number *" className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:border-brand-cyan text-sm" />
                              <input type="text" name="company" value={quoteForm.company} onChange={handleInputChange} placeholder="Company" className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:border-brand-cyan text-sm" />
                            </div>
                            <div>
                              <textarea name="notes" value={quoteForm.notes} onChange={handleInputChange} rows="3" placeholder="Additional Notes..." className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:border-brand-cyan text-sm resize-none"></textarea>
                            </div>

                            <button
                              type="submit"
                              disabled={isSubmitting}
                              className="w-full py-4 bg-brand-dark text-white font-bold rounded-xl hover:bg-brand-cyan hover:text-brand-dark transition-all duration-300 flex items-center justify-center gap-2 mt-4 disabled:opacity-70 disabled:cursor-not-allowed"
                            >
                              {isSubmitting ? (
                                <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                              ) : (
                                <>
                                  <Send size={20} /> Send Quote Request
                                </>
                              )}
                            </button>
                          </form>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        {/* Product Customization Modal (Existing) */}
        <AnimatePresence>
          {selectedProduct && (
            <div className="fixed inset-0 z-[70] flex items-center justify-center px-4">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={closeModal}
                className="absolute inset-0 bg-brand-dark/60 backdrop-blur-sm"
              ></motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="relative w-full max-w-4xl bg-white rounded-[2rem] shadow-2xl overflow-hidden z-10 flex flex-col md:flex-row max-h-[90vh]"
              >
                <button onClick={closeModal} className="absolute top-4 right-4 z-20 w-10 h-10 bg-white/80 backdrop-blur rounded-full flex items-center justify-center text-gray-500 hover:text-brand-dark hover:bg-gray-100 transition-colors">
                  <X size={20} />
                </button>

                <div className="w-full md:w-1/2 bg-gray-50 relative h-64 md:h-auto border-r border-gray-100">
                  <img src={selectedProduct.image} alt={selectedProduct.name} className="w-full h-full object-cover" />
                </div>

                <div className="w-full md:w-1/2 p-8 md:p-12 overflow-y-auto custom-scrollbar">
                  <div className="mb-2 text-brand-cyan font-bold text-sm tracking-wider uppercase">{selectedProduct.category}</div>
                  <h2 className="text-3xl font-heading font-black text-brand-dark mb-2">{selectedProduct.name}</h2>
                  <p className="text-gray-500 mb-8">{selectedProduct.desc}</p>

                  <div className="mb-8">
                    <h4 className="font-bold text-brand-dark mb-3">1. Upload Your Logo</h4>
                    <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-brand-cyan/50 rounded-xl bg-brand-light hover:bg-brand-cyan/10 transition-colors cursor-pointer relative overflow-hidden">
                      <input type="file" className="hidden" accept=".png,.jpg,.jpeg,.ai,.eps,.pdf" onChange={handleFileUpload} />
                      {uploadedFile ? (
                        <div className="text-center text-brand-dark font-bold flex flex-col items-center gap-2">
                          <CheckCircle size={32} className="text-green-500" />
                          {uploadedFile}
                        </div>
                      ) : (
                        <div className="text-center text-gray-500 flex flex-col items-center gap-2">
                          <UploadCloud size={32} className="text-brand-cyan" />
                          <span className="font-semibold text-brand-dark">Click to upload artwork</span>
                          <span className="text-xs">PNG, JPG, AI, EPS</span>
                        </div>
                      )}
                    </label>
                  </div>

                  <div className="mb-10">
                    <h4 className="font-bold text-brand-dark mb-3">2. Select Quantity</h4>
                    <div className="flex items-center gap-4">
                      <button
                        onClick={() => setQuantity(q => Math.max(10, q - 10))}
                        className="w-12 h-12 rounded-xl border border-gray-200 flex items-center justify-center hover:bg-gray-50 text-brand-dark"
                      >
                        <Minus size={20} />
                      </button>
                      <div className="flex-1 h-12 bg-gray-50 rounded-xl border border-gray-200 flex items-center justify-center font-black text-xl text-brand-dark">
                        {quantity}
                      </div>
                      <button
                        onClick={() => setQuantity(q => q + 10)}
                        className="w-12 h-12 rounded-xl border border-gray-200 flex items-center justify-center hover:bg-gray-50 text-brand-dark"
                      >
                        <Plus size={20} />
                      </button>
                    </div>
                    <p className="text-xs text-gray-400 mt-2 text-center">Minimum order quantity: 10</p>
                  </div>

                  <button
                    onClick={handleAddToCart}
                    className="w-full py-4 bg-brand-dark text-white font-black text-lg rounded-xl hover:bg-brand-cyan hover:text-brand-dark transition-colors duration-300 flex items-center justify-center gap-3 shadow-[0_10px_30px_rgba(11,25,143,0.2)]"
                  >
                    <ShoppingCart size={24} />
                    Add to Quote Request
                  </button>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
};

export default BrandingProductsPage;